/**
   * Locale dropdown and compact panel.
   *
   * Both are plain toggles: state lives in a data attribute the CSS reads, so
   * there is no inline style to fight and the transitions stay in the
   * stylesheet.
   */
  class NoorLocaleSelect extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-noor-locale-toggle]');
      if (!this.toggle) return;

      this.onToggle = () => this.setOpen(this.dataset.open !== 'true');
      this.onOutside = (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      };
      this.onKey = (event) => {
        if (event.key === 'Escape') this.setOpen(false);
      };

      this.toggle.addEventListener('click', this.onToggle);
      document.addEventListener('click', this.onOutside);
      document.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.onToggle);
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('keydown', this.onKey);
    }

    setOpen(open) {
      this.dataset.open = String(open);
      this.toggle.setAttribute('aria-expanded', String(open));
    }
  }

  if (!customElements.get('noor-locale-select')) {
    customElements.define('noor-locale-select', NoorLocaleSelect);
  }

  /** Account panel. Same toggle contract as the locale selector. */
  class NoorAccount extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-noor-account-toggle]');
      if (!this.toggle) return;

      this.onToggle = (event) => {
        event.stopPropagation();
        this.setOpen(this.dataset.open !== 'true');
      };
      this.onOutside = (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      };
      this.onKey = (event) => {
        if (event.key === 'Escape') this.setOpen(false);
      };

      this.toggle.addEventListener('click', this.onToggle);
      document.addEventListener('click', this.onOutside);
      document.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.onToggle);
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('keydown', this.onKey);
    }

    setOpen(open) {
      this.dataset.open = String(open);
      this.toggle.setAttribute('aria-expanded', String(open));
    }
  }

  if (!customElements.get('noor-account')) {
    customElements.define('noor-account', NoorAccount);
  }

  /**
   * Cart: the drawer opens through a binding relative to its own component,
   * which exposes no id, so the visible button forwards to the hidden one.
   */
  (() => {
    const trigger = document.querySelector('[data-noor-cart-open]');
    if (!trigger) return;
    trigger.addEventListener('click', () => {
      document.querySelector('cart-drawer-component > button')?.click();
    });
  })();

  (() => {
    const panel = document.querySelector('[data-noor-menu-panel]');
    const openButton = document.querySelector('[data-noor-menu-open]');
    if (!panel || !openButton) return;

    const setOpen = (open) => {
      // `hidden` is removed before animating so the transition can run.
      if (open) panel.removeAttribute('hidden');
      requestAnimationFrame(() => {
        panel.dataset.open = String(open);
      });
      openButton.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      if (!open) {
        setTimeout(() => {
          if (panel.dataset.open !== 'true') panel.setAttribute('hidden', '');
        }, 560);
      }
    };

    openButton.addEventListener('click', () => setOpen(true));
    panel.querySelector('[data-noor-menu-close]')?.addEventListener('click', () => setOpen(false));
    panel.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel.dataset.open === 'true') setOpen(false);
    });
  })();

/**
 * Cart count.
 *
 * The reference header carries no count; this store wants one. It listens to
 * the theme's own cart:update event, so it stays right no matter what added
 * the item — quick add, the product page or the drawer itself.
 */
(() => {
  const badge = document.querySelector('[data-noor-cart-count]');
  if (!badge) return;

  const render = (count) => {
    badge.textContent = String(count);
    badge.classList.toggle('is-empty', count === 0);
  };

  document.addEventListener('cart:update', async (event) => {
    const fromEvent = event.detail?.resource?.item_count;
    if (typeof fromEvent === 'number') {
      render(fromEvent);
      return;
    }
    // Some sources dispatch without the cart body: ask for it.
    try {
      const cart = await (await fetch('/cart.js')).json();
      render(cart.item_count);
    } catch (error) {
      // leave the last known count in place
    }
  });
})();
